// authSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { validateAndLoginThunk } from "../thunks/authThunk";
import secureLocalStorage from "react-secure-storage";

const initialState = {
  /* =========================
   * AUTH STATE
   * ========================= */
  isLoggedIn: null,
  accessToken: null,
  accessTokenExpiresOn: null,
  refreshToken: null,
  refreshTokenExpiresOn: null,

  lIU: null,
  lIUId: null,
  role: null,
  name: null,
  empID: null,
  userInfo: null,

  /* =========================
   * LIST / SEARCH STATE
   * ========================= */
  list: [],
  recordCountFor: null,
  recordCount: null,
  pagination: null,
  totalCount: null,

  /* =========================
   * UI STATE
   * ========================= */
  selectedRowKeysChildInfo: [],

  /* =========================
   * META
   * ========================= */
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,

  reducers: {
    /* =========================
     * LOGIN SUCCESS (OPTIONAL)
     * ========================= */
    loginSuccess: (state, action) => {
      const {
        lIU,
        accessToken,
        accessTokenExpiresOn,
        refreshToken,
        refreshTokenExpiresOn,
      } = action.payload;

      state.lIU = lIU;
      state.accessToken = accessToken;
      state.accessTokenExpiresOn = accessTokenExpiresOn;
      state.refreshToken = refreshToken ?? null;
      state.refreshTokenExpiresOn = refreshTokenExpiresOn ?? null;
    },

    /* =========================
     * EMAIL VERIFICATION SUCCESS
     * ========================= */
    emailVerificationSuccess: (state, action) => {
      const {
        isLoggedIn,
        lIUId,
        role,
        name,
        empID,        
        userInfo,
      } = action.payload;

      state.isLoggedIn = isLoggedIn;
      state.lIUId = lIUId;
      state.role = role;
      state.name = name;
      state.empID = empID;      
      state.userInfo = userInfo;
    },

    /* =========================
     * CLEAR REDUX (LOGOUT / ERROR)
     * ========================= */
    clearRedux: (state, action) => {
      const empID = action.payload;

      if (empID === "error" || state.empID === empID) {
        return initialState;
      }

      return state;
    },

    /* =========================
     * SEARCH INFO
     * ========================= */
    searchInfo: (state, action) => {
      const {
        list,
        recordCountFor,
        recordCount,
        pagination,
        totalCount,
      } = action.payload;

      state.list = list;
      state.recordCountFor = recordCountFor;
      state.recordCount = recordCount;
      state.pagination = pagination;
      state.totalCount = totalCount;
    },

    /* =========================
     * SELECTED ROW INFO
     * ========================= */
    selectedRowInfo: (state, action) => {
      state.selectedRowKeysChildInfo =
        action.payload.selectedRowKeysChildInfo || [];
    },
  },

  extraReducers: (builder) => {
    builder
      /* =========================
       * VALIDATE TOKEN
       * ========================= */
      .addCase(validateAndLoginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(validateAndLoginThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { accessToken, accessTokenExpiresOn, user, extraData } =
          action.payload;

        /* =========================
        * REDUX STATE
        * ========================= */
        state.isLoggedIn = true;
        state.accessToken = accessToken;
        state.accessTokenExpiresOn = accessTokenExpiresOn;

        state.lIU = user?.name || null;
        state.lIUId = user?._id || null;
        state.role = user?.role || null;
        state.name = user?.name || null;
        state.empID = user?.empID || null;
        state.userInfo = user || null;

        state.error = null;

         /* =========================
          * PERSISTENCE
          * ========================= */
        secureLocalStorage.setItem("accessToken", accessToken);
        secureLocalStorage.setItem("accessTokenExpiry", accessTokenExpiresOn);
        secureLocalStorage.setItem("role", user.role);
        secureLocalStorage.setItem("empID", user.empID);
        secureLocalStorage.setItem("lIUId", user._id);
        secureLocalStorage.setItem("lIU", user.name);
        secureLocalStorage.setItem("reportingSupervisor",user?.reportingSupervisor);             
        
        if (extraData?.logoutSuccessUrl) {
          secureLocalStorage.setItem(
            "logoutSuccessUrl",
            extraData.logoutSuccessUrl
          );
        }
      })

      .addCase(validateAndLoginThunk.rejected, (state, action) => {
        secureLocalStorage.clear();
        state.loading = false;
        state.isLoggedIn = false;
        state.error =
          action.payload?.message || "Token validation failed";
      });
  },
});

export const {
  loginSuccess,
  emailVerificationSuccess,
  clearRedux,
  searchInfo,
  selectedRowInfo,
} = authSlice.actions;

export default authSlice.reducer;
